// src/pages/OfflineGenAIProgramPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./OfflineGenAIProgramPage.css";
import { NeuralNoise } from "../components/ui/neural-noise";

const API_BASE_URL =
  "https://qlw95zx5ta.execute-api.eu-north-1.amazonaws.com/t/payment";

const fmtINR = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

const OfflineGenAIProgramPage = () => {
  // theme from your global data-theme
  const [theme, setTheme] = useState(
    document?.documentElement?.getAttribute("data-theme") || "dark"
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setTheme(el.getAttribute("data-theme") || "dark");
    update();
    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const noiseColor = theme === "light" ? [0.12, 0.35, 1.0] : [1.0, 0.65, 0.12];
  const noiseOpacity = theme === "light" ? 0.26 : 0.7;

  // ✅ Course name shown in payment area + success modal
  const courseName = "Gen-AI Professional Course";

  // program details (keep it concise on page)
  const program = useMemo(
    () => ({
      name: "Offline GenAI Product Internship Program",
      duration: "6–7 Months",
      badge: "OFFLINE • GENAI • PRODUCT INTERNSHIP",
      short:
        "A structured offline program with real product internship exposure—focused on building practical GenAI + engineering skills with mentorship.",
      highlights: [
        { k: "Schedule", v: "6 days/week • 3 days classes • 3 days internship" },
        { k: "Placement", v: "Placement assistance after ~50% completion" },
        { k: "Interviews", v: "Minimum 3 interviews based on eligibility" },
      ],
      points: [
        "Candidate will be part of a product team",
        "Vendor empanelment is planned (starting Jan).",
        "Mentor-led classes + internship execution on real tasks.",
      ],
      whatYouGet: [
        "Mentorship & code reviews",
        "Portfolio-ready project work",
        "Internship experience letter by Inikola.",
        "Career guidance + interview support",
      ],
      curriculumPills: [
        "Python + APIs",
        "Data Engineering",
        "SQL",
        "ML Foundations",
        "Deep Learning",
        "NLP + Transformers",
        "RAG + LangChain",
        "Agentic Systems",
      ],
      note:
        "Curriculum shown as high-level modules only. Detailed weekly plan is shared after registration.",
      stipendNote:
        "Top 10% candidates can receive ₹10,000/month stipend based on monthly performance.",
      advisor: {
        name: "Mr. Mohan Krishna Bitta",
        title: "Program Advisor",
        phoneDisplay: "+91 93815 84179",
        phoneHref: "tel:+919381584179",
      },
    }),
    []
  );

  /**
   * ✅ Updated to match backend:
   * pay_mode: "upi" OR "card_nb"
   * Backend will compute final amount if pay_mode="card_nb":
   * final = base + base*0.02 + (base*0.02)*0.18  => base*1.0236
   */
  const [feeMode, setFeeMode] = useState("without_gst"); // without_gst | with_gst
  const [payMode, setPayMode] = useState("upi"); // "upi" | "card_nb" (matches backend)

  // YOUR program fee toggle (keep as-is)
  const baseAmount = feeMode === "with_gst" ? 116820 : 99000;

  // UI only: show estimated charges for card/netbanking (backend is source of truth)
  const chargesBreakdown = useMemo(() => {
    if (payMode !== "card_nb") {
      return {
        convenienceFee: 0,
        gstOnFee: 0,
        totalCharges: 0,
        estimatedFinal: baseAmount,
      };
    }
    const convenienceFee = Math.round(baseAmount * 0.02);
    const gstOnFee = Math.round(convenienceFee * 0.18);
    const totalCharges = convenienceFee + gstOnFee;
    const estimatedFinal = baseAmount + totalCharges;
    return { convenienceFee, gstOnFee, totalCharges, estimatedFinal };
  }, [payMode, baseAmount]);

  const totalPayable = chargesBreakdown.estimatedFinal;

  // form
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const canPay =
    formData.fullName &&
    formData.email &&
    formData.phoneNumber &&
    acceptedTerms &&
    !loading;

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // Razorpay integration (keep payload same)
  const createPaymentOrder = async () => {
    setLoading(true);
    setPaymentStatus("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/create_order/create-order`,
        {
          email: formData.email,
          amount: baseAmount, // ✅ BASE AMOUNT (backend computes final based on pay_mode)
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          courseName: program.name, // ✅ keep your existing backend behavior untouched
          pay_mode: payMode, // ✅ "upi" OR "card_nb"

          mode: "offline-genai",
          feeMode,
          ui_estimated_total: totalPayable,
        }
      );

      if (response.data?.status === "success") return response.data;
      throw new Error(response.data?.message || "Failed to create order");
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentData) => {
    const response = await axios.post(
      `${API_BASE_URL}/verify_payment/verify-payment`,
      {
        razorpay_order_id: paymentData.razorpay_order_id,
        razorpay_payment_id: paymentData.razorpay_payment_id,
        razorpay_signature: paymentData.razorpay_signature,
      }
    );
    return response.data;
  };

  const openRazorpay = (orderData) => {
    const am = orderData.allowed_methods || {};

    const options = {
      key: orderData.key,
      amount: orderData.amount, // from backend (final amount in paise)
      currency: orderData.currency,
      name: orderData.name,
      description: orderData.description,
      image: orderData.image,
      order_id: orderData.order_id,
      prefill: orderData.prefill,
      theme: orderData.theme,

      method: {
        upi: !!am.upi,
        card: !!am.card,
        netbanking: !!am.netbanking,
        wallet: false,
      },

      handler: async function (response) {
        try {
          setLoading(true);
          setPaymentStatus("Verifying payment...");
          const verification = await verifyPayment(response);

          if (verification?.status === "success") {
            setPaymentStatus("Payment successful!");
            setShowSuccessModal(true);
          } else {
            setPaymentStatus("Payment verification failed");
            alert("Payment verification failed. Please contact support.");
          }
        } catch (err) {
          console.error(err);
          setPaymentStatus("Payment verification failed");
          alert("Payment verification failed. Please contact support.");
        } finally {
          setLoading(false);
        }
      },

      modal: {
        ondismiss: function () {
          setPaymentStatus("Payment cancelled");
          setLoading(false);
        },
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", function (response) {
      setPaymentStatus("Payment failed");
      setLoading(false);
      alert(`Payment failed: ${response?.error?.description || "Unknown error"}`);
    });

    rzp.open();
  };

  const handlePay = async () => {
    if (!formData.fullName || !formData.email || !formData.phoneNumber) {
      alert("⚠ Please fill in Full Name, Email, Phone Number.");
      return;
    }
    if (!acceptedTerms) {
      alert("⚠ Please accept Terms and Conditions.");
      return;
    }

    try {
      setLoading(true);
      setPaymentStatus("Creating order...");
      const order = await createPaymentOrder();
      setPaymentStatus("Opening payment gateway...");
      openRazorpay(order);
    } catch (err) {
      console.error(err);
      setPaymentStatus("Error initiating payment");
      alert("Error initiating payment. Please try again.");
      setLoading(false);
    }
  };

  const scrollToPayment = () => {
    document.querySelector("#ogPayment")?.scrollIntoView({ behavior: "smooth" });
  };

  const resetAfterSuccess = () => {
    setShowSuccessModal(false);
    setPaymentStatus("");
    setAcceptedTerms(false);
    setFeeMode("without_gst");
    setPayMode("upi");
    setFormData({ fullName: "", email: "", phoneNumber: "" });
  };

  return (
    <div className="ogPage">
      <NeuralNoise
        color={noiseColor}
        opacity={noiseOpacity}
        speed={0.00035}
        className="ogNoise"
      />

      <div className="ogContent">
        {/* HERO */}
        <header className="ogHero">
          <div className="ogHeroInner">
            <div className="ogHeroLeft">
              <span className="ogBadge">{program.badge}</span>

              <h1 className="ogTitle">
                Learn <span className="ogGrad">GenAI</span> with Real{" "}
                <span className="ogGrad">Product Internship</span>
              </h1>

              <p className="ogSub">{program.short}</p>

              {/* Quick chips */}
              <div className="ogQuickRow">
                <div className="ogChip">
                  <span className="ogChipK">Course</span>
                  <span className="ogChipV">{courseName}</span>
                </div>
                <div className="ogChip">
                  <span className="ogChipK">Duration</span>
                  <span className="ogChipV">{program.duration}</span>
                </div>
                <div className="ogChip">
                  <span className="ogChipK">Structure</span>
                  <span className="ogChipV">
                    3 Days Class + 3 Days Internship
                  </span>
                </div>
              </div>

              <div className="ogHighlights">
                {program.highlights.map((h) => (
                  <div className="ogHiCard" key={h.k}>
                    <div className="ogHiTop">{h.k}</div>
                    <div className="ogHiBottom">{h.v}</div>
                  </div>
                ))}
              </div>

              {/* NEW: Stipend badge */}
              <div className="ogStipendBanner">
                <div className="ogStipendIcon">🏅</div>
                <div className="ogStipendText">
                  <div className="ogStipendTitle">Performance Stipend</div>
                  <div className="ogStipendSub">{program.stipendNote}</div>
                </div>
              </div>

              <div className="ogHeroActions">
                <button
                  className="ogPrimaryBtn"
                  onClick={scrollToPayment}
                  type="button"
                >
                  Register & Pay →
                </button>
                <a className="ogGhostBtn" href={program.advisor.phoneHref}>
                  Talk to Advisor • {program.advisor.phoneDisplay}
                </a>
              </div>

              <div className="ogNote">
                * Placement assistance is provided as per eligibility (no job
                guarantee).
              </div>
            </div>

            {/* PAYMENT */}
            <aside className="ogPayCol" id="ogPayment">
              <div className="ogPayCard">
                <div className="ogPayHeader">
                  <div className="ogPayTitle">One-Time Payment</div>
                  <div className="ogPayMini">
                    Choose GST option + payment method.
                  </div>
                </div>

                {/* ✅ Course info shown in registration */}
                <div className="ogCourseStrip">
                  <div className="ogCourseStripTop">Selected Course</div>
                  <div className="ogCourseStripName">{courseName}</div>
                  <div className="ogCourseStripMeta">
                    Offline • {program.duration} • Product Internship
                  </div>
                </div>

                {/* GST Toggle */}
                <div className="ogToggleWrap">
                  <button
                    type="button"
                    className={`ogToggleBtn ${
                      feeMode === "without_gst" ? "active" : ""
                    }`}
                    onClick={() => setFeeMode("without_gst")}
                  >
                    Without GST
                  </button>
                  <button
                    type="button"
                    className={`ogToggleBtn ${
                      feeMode === "with_gst" ? "active" : ""
                    }`}
                    onClick={() => setFeeMode("with_gst")}
                  >
                    With GST
                  </button>
                </div>

                {/* Method */}
                <div className="ogField">
                  <label>Payment Method</label>
                  <div className="ogMethodGrid">
                    <button
                      type="button"
                      className={`ogMethod ${payMode === "upi" ? "active" : ""}`}
                      onClick={() => setPayMode("upi")}
                    >
                      UPI (No extra charges)
                      <div className="ogMiniLine">Recommended</div>
                    </button>

                    <button
                      type="button"
                      className={`ogMethod ${
                        payMode === "card_nb" ? "active" : ""
                      }`}
                      onClick={() => setPayMode("card_nb")}
                    >
                      Card / NetBanking
                      <div className="ogMiniLine">
                        Convenience fee + GST applied
                      </div>
                    </button>
                  </div>
                  <div className="ogHint">
                    For Card/NetBanking, charges are: 2% convenience fee + 18%
                    GST on fee.
                  </div>
                </div>

                {/* Breakdown */}
                <div className="ogBreakdown">
                  <div className="row">
                    <span>Program Fee</span>
                    <span>₹{fmtINR(baseAmount)}</span>
                  </div>

                  <div className="row">
                    <span>Convenience Fee (2%)</span>
                    <span>₹{fmtINR(chargesBreakdown.convenienceFee)}</span>
                  </div>

                  <div className="row">
                    <span>GST on Fee (18%)</span>
                    <span>₹{fmtINR(chargesBreakdown.gstOnFee)}</span>
                  </div>

                  <div className="row">
                    <span>Payment Charges</span>
                    <span>₹{fmtINR(chargesBreakdown.totalCharges)}</span>
                  </div>

                  <div className="row total">
                    <span>Total Payable</span>
                    <span>₹{fmtINR(totalPayable)}</span>
                  </div>

                  <div className="ogHint" style={{ marginTop: 8 }}>
                    Note: Final payable in Razorpay will be based on the backend
                    calculation for the selected payment mode.
                  </div>
                </div>

                {/* Form */}
                <div className="ogForm">
                  <div className="ogInputRow">
                    <input
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInput}
                      placeholder="Full Name"
                      autoComplete="name"
                    />
                    <input
                      name="email"
                      value={formData.email}
                      onChange={handleInput}
                      placeholder="Email Address"
                      autoComplete="email"
                    />
                    <input
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInput}
                      placeholder="Phone Number"
                      autoComplete="tel"
                    />
                  </div>

                  <label className="ogTerms">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/terms-and-conditions"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms & Conditions
                      </a>{" "}
                      and{" "}
                      <a href="/privacy-policy" target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>

                  <button
                    className="ogPayBtn"
                    disabled={!canPay}
                    onClick={handlePay}
                    type="button"
                  >
                    {loading ? "Processing..." : "Proceed to Payment →"}
                  </button>

                  {paymentStatus ? (
                    <div className="ogStatus">{paymentStatus}</div>
                  ) : null}

                  <div className="ogSecure">🔒 Secured by Razorpay</div>
                </div>
              </div>
            </aside>
          </div>
        </header>

        {/* NEW: Advisor section */}
        <section className="ogAdvisor">
          <div className="ogAdvisorInner">
            <div className="ogAdvisorCard">
              <div className="ogAdvisorLeft">
                <div className="ogAdvisorAvatar" aria-hidden="true">
                  MK
                </div>
                <div className="ogAdvisorInfo">
                  <div className="ogAdvisorLabel">Your Advisor</div>
                  <div className="ogAdvisorName">{program.advisor.name}</div>
                  <div className="ogAdvisorRole">{program.advisor.title}</div>
                  <div className="ogAdvisorDesc">
                    Get clarity on eligibility, batch schedule, onboarding steps
                    and payment support.
                  </div>
                </div>
              </div>

              <div className="ogAdvisorRight">
                <a className="ogAdvisorBtn" href={program.advisor.phoneHref}>
                  Call / WhatsApp • {program.advisor.phoneDisplay}
                </a>
                <div className="ogAdvisorMeta">
                  Available 10am – 7pm (Mon–Sat)
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DETAILS */}
        <section className="ogSection">
          <div className="ogSectionInner">
            <h2 className="ogH2">
              Program <span className="ogGradSmall">Details</span>
            </h2>

            <div className="ogGrid3">
              <div className="ogInfoCard">
                <div className="ogInfoTitle">Weekly Structure</div>
                <ul>
                  <li>6 Days in a week</li>
                  <li>3 days mentor-led classes</li>
                  <li>3 days product internship execution</li>
                  <li>Regular evaluation & feedback</li>
                </ul>
              </div>

              <div className="ogInfoCard">
                <div className="ogInfoTitle">Career Support</div>
                <ul>
                  <li>Placement assistance after ~50% course completion</li>
                  <li>Eligibility-based interview scheduling</li>
                  <li>Minimum 3 interviews (based on eligibility)</li>
                  <li>Resume & portfolio guidance</li>
                </ul>
              </div>

              <div className="ogInfoCard">
                <div className="ogInfoTitle">Team Exposure</div>
                <ul>
                  <li>Work with product-based teams</li>
                  <li>Real tasks, real timelines</li>
                  <li>Mentorship from industry professionals</li>
                </ul>
              </div>
            </div>

            <div className="ogBulletGrid">
              <div className="ogBulletCard">
                <div className="ogBulletTitle">What you get</div>
                <div className="ogBulletList">
                  {program.whatYouGet.map((x) => (
                    <div key={x} className="ogBulletItem">
                      <span className="dot" />
                      <span>{x}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ogBulletCard">
                <div className="ogBulletTitle">Important Notes</div>
                <div className="ogBulletList">
                  {program.points.map((x) => (
                    <div key={x} className="ogBulletItem">
                      <span className="dot" />
                      <span>{x}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ogCurr">
              <div className="ogCurrHead">
                <div className="ogCurrTitle">High-level modules</div>
                <div className="ogCurrNote">{program.note}</div>
              </div>

              <div className="ogCurrStrip">
                {program.curriculumPills.map((p) => (
                  <span className="pill" key={p}>
                    {p}
                  </span>
                ))}
              </div>
            </div>

            <div className="ogSmallNote">
              For complete weekly schedule, our team shares the detailed plan
              after registration.
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="ogCta">
          <div className="ogCtaInner">
            <div>
              <div className="ogCtaTitle">Ready to enroll?</div>
              <div className="ogCtaSub">
                Lock your seat with a one-time payment via Razorpay checkout.
                <br />
                Need help? Call/WhatsApp:{" "}
                <a className="ogInlineLink" href={program.advisor.phoneHref}>
                  {program.advisor.phoneDisplay}
                </a>
              </div>
            </div>

            <button className="ogCtaBtn" type="button" onClick={scrollToPayment}>
              Register Now →
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="ogFooter">
          <div className="ogFooterInner">
            <span>© {new Date().getFullYear()}</span>
            <span>
              Need help?{" "}
              <a href={program.advisor.phoneHref}>{program.advisor.phoneDisplay}</a>
            </span>
          </div>
        </footer>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="ogModalBackdrop" role="dialog" aria-modal="true">
            <div className="ogModal">
              <h2>Payment Successful ✅</h2>
              <p>
                We received your payment. Our team will contact you shortly with
                onboarding steps.
              </p>

              <div className="ogModalBox">
                <div>
                  <b>Course:</b> {courseName}
                </div>
                <div>
                  <b>Program:</b> {program.name}
                </div>
                <div>
                  <b>Fee Mode:</b>{" "}
                  {feeMode === "with_gst" ? "With GST" : "Without GST"}
                </div>
                <div>
                  <b>Payment Mode:</b>{" "}
                  {payMode === "upi" ? "UPI" : "Card / NetBanking"}
                </div>
                <div>
                  <b>Estimated Total:</b> ₹{fmtINR(totalPayable)}
                </div>
                <div>
                  <b>Name:</b> {formData.fullName}
                </div>
              </div>

              <button className="ogModalBtn" type="button" onClick={resetAfterSuccess}>
                Continue
              </button>

              <button
                className="ogModalBtnSecondary"
                type="button"
                onClick={() => (window.location.href = "/")}
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineGenAIProgramPage;
